import { useState, useCallback, useMemo, useEffect, useRef, useLayoutEffect } from "react";
import {
  Calendar, Flag, CircleStop, Users, CircleDot, X, ClockFading, ChevronRight,
  CircleCheck, SendHorizontal, MoveLeft, Pencil, Info
} from "lucide-react";
import EmployeeLayout from "../EmployeeLayout";
import { useUser, useProjects, useUpdateProject, useGetPermissions, useComments, useAddComment, useFilterEmployee, useTotalStaff } from "../../Use-auth";
import ProjectList from "../../ui/ProjectList";
import TaskEmployees from "../../ui/TaskEmployees";
import TaskPriority from "../../ui/TaskPriority";
import CommentsSection from "../../ui/CommentsSection ";
import TaskDetails from "../../ui/TaskDetails ";
import TaskTimeline from "../../ui/TaskTimeline ";
import SkeletonPage from "../../ui/SkeletonPage "
import MyTask from "../../ui/MyTask";
import ProjectTab from "../../ui/ProjectTab";
import PermissionPopup from "../../ui/PermissionPopup";
import { getStatusColor } from "../utils/taskUtils";
import { useProjectRealtime } from "../../ui/Projects/useProjectRealtime";

export default function EmployeeProject() {
  const { data: filteredEmployees } = useFilterEmployee();
  const { data: totalStaff } = useTotalStaff();
  const { data: user } = useUser();
  useProjectRealtime(user?.userId);
  const { data: projects = [], refetch } = useProjects();
  const updateProject = useUpdateProject();
  const { data: existingPermissions } = useGetPermissions();
  const [collapsedStatuses, setCollapsedStatuses] = useState({});
  const [showDescriptionPopup, setShowDescriptionPopup] = useState(false);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [currentTask, setCurrentTask] = useState(null);
  const [status, setStatus] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState([]);
  const [commentText, setCommentText] = useState("");
  const addComment = useAddComment();
  const [commentEmployees, setCommentEmployees] = useState([]);
  const [runningTaskId, setRunningTaskId] = useState(null);
  const [active, setActive] = useState("mytasks");
  const [showModal, setShowModal] = useState(false);
  const popupRef = useRef(null);
  const [orderedProjects, setOrderedProjects] = useState(projects || []);
  const [selectProjectFilter, setSelectProjectFilter] = useState(() => {
    const filter = new URLSearchParams(window.location.search).get("filter") || "";
    return filter === "all" ? "" : filter;
  });
  const isEmployee = ["Development", "Design", "Sales"].includes(user?.department);

  const [searchTerm, setSearchTerm] = useState("");

  const [editData, setEditData] = useState({
    title: "",
    description: [
      {
        storeLink: "",
        referenceLink: "",
        referenceLinkEnabled: false,
        figmaLink: "",
        figmaLinkDisabled: false,
        taskdescription: [],
        files: [],
      },
    ],
    priority: "",
    status: status,
    assignedEmployees: [],
    dueDate: null
  });

  const [showStatusDropdownFor, setShowStatusDropdownFor] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState({});
  const [allStatuses, setAllStatuses] = useState([]);
  const textareaRef = useRef(null);
  const [activeSubProjectId, setActiveSubProjectId] = useState(null);
  const statusDropdownRef = useRef(null);

  const currentUserPermissions = existingPermissions?.find(
    (p) => p.userId === user?.userId
  );

  const status_update = currentUserPermissions?.status_update ?? currentUserPermissions?.employees?.status_update ?? false;
  const project_view = currentUserPermissions?.project_view ?? currentUserPermissions?.employees?.project_view ?? false;
  const projectIds = currentUserPermissions?.project_id ?? currentUserPermissions?.employees?.project_id ?? [];

  useLayoutEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [editData.title]);

  // Close status dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        showStatusDropdownFor &&
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(e.target)
      ) {
        setShowStatusDropdownFor(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [showStatusDropdownFor]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (showStatusDropdownFor) {
        const dropdown = document.getElementById(`status-dropdown-${showStatusDropdownFor}`);
        if (dropdown && !dropdown.contains(event.target)) {
          setShowStatusDropdownFor(null);
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showStatusDropdownFor]);

  const activeProject = useMemo(
    () => projects.find((p) => String(p._id || p.id) === String(activeProjectId)),
    [projects, activeProjectId]
  );

  const activeSubProjectObj = useMemo(() => {
    if (!activeProject?.isMultiProject) return null;
    if (!activeSubProjectId) return null;

    return (
      activeProject.subProjects?.find(
        (sp) => String(sp.id) === String(activeSubProjectId)
      ) || null
    );
  }, [activeProject, activeSubProjectId]);

  const selectedProjectData = activeProject;

  useEffect(() => {
    let statuses = [];

    if (!activeProjectId || !projects.length) {
      statuses = [];
    } else {
      const activeProject = projects.find(
        (p) => p._id?.toString() === activeProjectId?.toString()
      );

      if (!activeProject) {
        statuses = [];
      } else if (activeProject.isMultiProject) {
        if (!activeSubProjectId) {
          statuses = [];
        } else {
          const subProject = activeProject.subProjects?.find(
            (sp) => sp._id?.toString() === activeSubProjectId?.toString()
          );

          statuses = subProject?.statusTask
            ? Object.keys(subProject.statusTask)
            : [];
        }
      } else {
        statuses = activeProject.statusOrder || [];
      }
    }

    setAllStatuses((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(statuses)) return prev;
      return statuses;
    });
  }, [projects, activeProjectId, activeSubProjectId]);

  useEffect(() => {
    if (
      activeProject?.isMultiProject &&
      activeProject.subProjects?.length &&
      !activeSubProjectId
    ) {
    }
  }, [activeProject, activeSubProjectId]);

  // useEffect(() => {
  //   setActiveSubProjectId(null);
  // }, [activeProjectId]);

  const currentStatusTask = useMemo(() => {
    if (!activeProject) return {};

    if (activeProject.isMultiProject) {
      if (!activeSubProjectObj) return {};

      const allStatuses = activeSubProjectObj.statusOrder || [];
      const taskMap = activeSubProjectObj.statusTask || {};

      const result = {};

      allStatuses.forEach((status) => {
        result[status] = (taskMap[status] || []).filter(
          (task) =>
            String(task.subProjectId) === String(activeSubProjectId)
        );
      });

      return result;
    }

    const allStatuses = activeProject.statusOrder || [];
    const taskMap = activeProject.statusTask || {};

    const result = {};

    allStatuses.forEach((status) => {
      result[status] = (taskMap[status] || []).filter(
        (task) => !task.subProjectId
      );
    });

    return result;
  }, [activeProject, activeSubProjectObj, activeSubProjectId]);

  useEffect(() => {
    if (!currentStatusTask || Object.keys(currentStatusTask).length === 0) {
      setProjectStatuses([]);
      return;
    }

    setProjectStatuses(Object.keys(currentStatusTask));
  }, [currentStatusTask]);

  const handleOpenTask = (task) => {
    setCurrentTask(task);

    const data = {
      title: task.title || "",
      description: task.description || "",
      priority: task.priority || "",
      status: task.status || "",
      dueDate: task.dueDate ? new Date(task.dueDate) : null,
      assignedEmployees: task.assignedEmployees || [],
      comments: task.comments || [],
      timeline: task.timeline || []
    };
    setEditData(data);
    setShowDescriptionPopup(true);
  };

  const localProjects = useMemo(() => projects || [], [projects]);

  const nowIST = new Date().toLocaleString("sv-SE", {
    timeZone: "Asia/Kolkata",
  }).replace(" ", "T");

  const handleBulkStatusUpdate = useCallback(
    async (newStatus, singleTask = null) => {
      const currentUserName = user?.fullName || "Unknown";

      const selectedTaskObjects = singleTask
        ? [singleTask]
        : Object.values(selectedTasks)
          .flat()
          .map((id) => {
            for (const project of localProjects) {
              const directTask = Object.values(project.statusTask || {})
                .flat()
                .find((t) => t._id === id);
              if (directTask)
                return { ...directTask, projectId: project._id, subProjectId: null };

              for (const sub of project.subProjects || []) {
                const subTask = Object.values(sub.statusTask || {})
                  .flat()
                  .find((t) => t._id === id);
                if (subTask)
                  return { ...subTask, projectId: project._id, subProjectId: sub._id };
              }
            }
            return null;
          })
          .filter(Boolean);

      const uniqueTasks = Array.from(
        new Map(selectedTaskObjects.map((t) => [t._id, t])).values()
      );

      try {
        for (const task of uniqueTasks) {
          const project = localProjects.find((p) => p._id === task.projectId);
          if (!project) continue;

          let latestTask;
          if (task.subProjectId) {
            const sub = project.subProjects?.find((s) => s._id === task.subProjectId);
            latestTask = Object.values(sub?.statusTask || {})
              .flat()
              .find((t) => t._id === task._id);
          } else {
            latestTask = Object.values(project.statusTask || {})
              .flat()
              .find((t) => t._id === task._id);
          }

          if (!latestTask) continue;

          let comments = [];
          let timelineUpdate = null;

          if (newStatus === "review") {
            const runningTimeline = latestTask.timeline?.find(
              (tl) =>
                tl.employeeId === user.userId &&
                tl.startTime &&
                !tl.endTime
            );

            if (runningTimeline) {
              timelineUpdate = {
                employeeId: user.userId,
                endTime: getLocalDate(),
              };

              comments.push({
                text: `${currentUserName} stopped timer (auto) due to review status`,
                createdBy: currentUserName,
                createdAt: nowIST,
              });

              setRunningTaskId(null);
            }
          }

          if (latestTask.status !== newStatus) {
            comments.push({
              text: `${currentUserName} changed status from ${latestTask.status} to ${newStatus}`,
              createdBy: currentUserName,
              createdAt: nowIST,
            });
          }

          await updateProject.mutateAsync({
            id: task.projectId,
            subProjectId: task.subProjectId || null,
            taskId: task._id,
            status: newStatus,
            comments,
            timelineUpdate,
          });
        }

        setSelectedTasks({});
        refetch();
      } catch (err) {
        console.error("Bulk status update failed:", err);
      }
    },
    [selectedTasks, user, updateProject, refetch, localProjects]
  );

  const formatStatusLabel = (status = "") =>
    status
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const getLocalDate = () => {
    const now = new Date();

    const pad = (n) => n.toString().padStart(2, "0");

    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
      now.getDate()
    )} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(
      now.getSeconds()
    )}`;
  };

  const allTasks = useMemo(() => {
    return projects.flatMap((project) => {
      let tasks = [];

      if (project.statusTask) {
        tasks.push(...Object.values(project.statusTask).flat());
      }

      if (project.subProjects) {
        project.subProjects.forEach((sub) => {
          if (sub.statusTask) {
            tasks.push(...Object.values(sub.statusTask).flat());
          }
        });
      }

      return tasks;
    });
  }, [projects]);

  const handleTimelineUpdate = useCallback(
    async (task, type) => {
      if (!user?.userId) return false;

      const hasGlobalRunning = allTasks?.some((t) =>
        t.timeline?.some(
          (tl) =>
            tl.employeeId === user.userId &&
            tl.startTime &&
            !tl.endTime
        )
      );

      let timelineUpdate = {};
      let newStatus = task.status;
      let commentText = "";

      if (type === "start") {
        if (hasGlobalRunning && runningTaskId !== task._id) {
          alert("You already have a running task.");
          return false;
        }

        timelineUpdate = {
          employeeId: user.userId,
          startTime: getLocalDate(),
        };

        commentText = `${user.fullName} started working on the task`;

        setRunningTaskId(task._id);

        if (task.status === "pending") {
          newStatus = "progress";

          await handleBulkStatusUpdate("progress", {
            ...task,
            projectId: task.projectId || activeProjectId,
            subProjectId: task.subProjectId || null,
          });
        }
      }

      if (type === "stop") {
        timelineUpdate = {
          employeeId: user.userId,
          endTime: getLocalDate(),
        };

        commentText = `${user.fullName} stopped working on the task`;

        setRunningTaskId(null);
      }

      if (!timelineUpdate.employeeId) return false;

      const newComment = {
        text: commentText,
        createdBy: user.fullName,
        createdAt: getLocalDate(),
      };

      try {
        await updateProject.mutateAsync({
          id: task.projectId || activeProjectId,
          subProjectId: task.subProjectId || null,
          taskId: task._id,
          status: newStatus,
          timelineUpdate,
          comments: [newComment],
        });

        await refetch();

        return true;
      } catch (err) {
        console.error("ERROR:", err);
        return false;
      }
    },
    [
      user,
      updateProject,
      refetch,
      runningTaskId,
      activeProjectId,
      handleBulkStatusUpdate,
      allTasks,
    ]
  );

  const truncateResponsive = (text) => {
    if (!text) return "";
    const limit = window.innerWidth < 640 ? 15 : 40;
    return text.length > limit ? text.slice(0, limit) + "..." : text;
  };

  const renderTasks = useCallback(
    (status) => {
      const tasks = (currentStatusTask[status] || [])
        .filter(
          (t) =>
            !activeProject?.isMultiProject ||
            String(t.subProjectId) === String(activeSubProjectId)
        )
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      if (!tasks.length) return null;

      return (
        <div className="space-y-2">
          <div className="
  grid grid-cols-3
  zx:grid-cols-4
  sm:grid-cols-5
  custom:grid-cols-5
  lg:grid-cols-6
  gap-2
  px-3 py-2 mt-[10px]
  rounded-md font-medium
  text-gray-500 text-[12px] sm:text-[14px]
">
            <div>Name</div>
            <div>Assigned</div>
            <div className="hidden custom:block">Due Date</div>
            <div className="hidden zx:block">Priority</div>
            <div className="hidden rs:block">Status</div>
            <div>Track Time</div>
          </div>

          {tasks.map((task) => (
            <div
              key={task._id}
              className="
    grid grid-cols-3
    zx:grid-cols-4
    sm:grid-cols-5
    custom:grid-cols-5
    lg:grid-cols-6
    gap-2
    items-center
    text-[12px] sm:text-[14px]
    bg-white
    px-2 sm:px-3
    py-2
    rounded-md
    shadow-sm
    hover:bg-gray-50
    cursor-pointer
    mb-2
  "
            >
              <div
                className="flex items-center gap-2 overflow-hidden"
                onClick={() => handleOpenTask(task)}
              >
                <div className="flex-shrink-0 rounded-full bg-[#299764] text-white p-1">
                  <CircleCheck size={6} />
                </div>

                <span
                  className="truncate text-gray-800 hover:text-blue-700 hover:underline"
                  title={task.title}
                >
                  {truncateResponsive(task.title)}
                </span>
              </div>

              <div>
                <TaskEmployees
                  selected={task.assignedEmployees?.map(e => e.userId) || []}
                  employees={totalStaff || []}
                  showDropdown={false}
                  onChange={() => { }}
                  hideSelected
                />
              </div>

              <div className="hidden custom:block text-gray-600 text-sm">
                {task.dueDate
                  ? new Date(task.dueDate).toLocaleString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                  : "-"}
              </div>

              <div className="hidden zx:block">
                <TaskPriority value={task.priority} showclear={false} />
              </div>

              <div className=" relative hidden rs:block ">
                <div className="flex items-center gap-2 text-[12px] sm:text-[14px] font-medium text-gray-700">
                  <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-800">
                    {task.status ? formatStatusLabel(task.status) : "-"}
                  </span>

                  {(
                    task?.assignedEmployees?.some(emp => emp.userId === user?.userId) ||
                    ["Admin", "Management"].includes(user?.department) ||
                    task?.createdBy?.userId === user?.userId
                  ) && task.status !== "complete" && (
                      <Pencil
                        size={14}
                        className="cursor-pointer text-orange-500 hover:text-orange-600 hover:scale-110 transition-all"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowStatusDropdownFor(prev =>
                            prev === task._id ? null : task._id
                          );
                        }}
                      />
                    )}
                </div>

                {status_update && showStatusDropdownFor === task._id && (
                  <div
                    ref={statusDropdownRef}
                    className="
        absolute left-0 mt-2 w-40
      max-h-56
        bg-white
        border border-gray-200
        rounded-xl
        shadow-xl
        overflow-hidden
        overflow-y-auto
        z-50
        flex flex-col
        animate-fadeIn
      "
                  >

                    {allStatuses
                      .filter((s) => s !== "complete")
                      .map((s) => (
                        <div
                          key={s}
                          onClick={() => {
                            handleBulkStatusUpdate(s, {
                              ...task,
                              projectId: activeProjectId,
                              subProjectId: activeSubProjectObj?._id || null,
                            });
                            setShowStatusDropdownFor(null);
                          }}
                          className={`
            px-4 py-2.5 text-sm
            flex items-center justify-between
            cursor-pointer transition-all
            ${s === task.status
                              ? "bg-gray-800 text-white font-semibold"
                              : "text-gray-700 hover:bg-gray-100"
                            }
          `}
                        >
                          <span>{formatStatusLabel(s)}</span>

                          {s === task.status && (
                            <span className="text-xs opacity-80">✓</span>
                          )}
                        </div>
                      ))}

                  </div>
                )}
              </div>

              <div className="">
                <TaskTimeline
                  task={{
                    ...task,
                    projectId: activeProjectId,
                    subProjectId: activeSubProjectObj?._id || null,
                  }}
                  user={user}
                  handleTimelineUpdate={handleTimelineUpdate}
                  showControls
                  runningTaskId={runningTaskId}
                  employee={totalStaff}
                />
              </div>
            </div>
          ))}
        </div>
      );
    },
    [
      activeProject,
      activeSubProjectObj,
      showStatusDropdownFor,
      status_update,
      allStatuses,
      handleBulkStatusUpdate,
      handleOpenTask,
      handleTimelineUpdate,
      user,
    ]
  );

  const toggleStatus = (status) => {
    setCollapsedStatuses((prev) => ({
      ...prev,
      [status]: !prev[status],
    }));
  };

  const getTaskCount = (status) =>
    currentStatusTask?.[status]?.length || 0;

  const renderSection = (status) => {
    const tasks = currentStatusTask[status] || [];

    return (
      <div className="mb-8">
        <div className="flex items-center gap-2">
          <ChevronRight
            size={18}
            color="grey"
            className={`cursor-pointer transition-transform ${collapsedStatuses[status] ? "" : "rotate-90"
              }`}
            onClick={() => toggleStatus(status)}
          />

          <h2 className={`text-[12px] sm:text-[14px] px-2 py-1 rounded ${getStatusColor(status)}`}>
            {formatStatusLabel(status)}
          </h2>
          <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">
            {getTaskCount(status)}
          </span>
        </div>

        {!collapsedStatuses[status] && renderTasks(status)}
      </div>
    );
  };

  const findSubProjectIdByTask = (projects, taskId) => {
    for (const project of projects || []) {
      if (!project.subProjects) continue;

      for (const sub of project.subProjects) {
        const statusTask = sub.statusTask || {};

        for (const tasks of Object.values(statusTask)) {
          if (!Array.isArray(tasks)) continue;

          if (tasks.some((t) => t._id === taskId)) {
            return sub._id;
          }
        }
      }
    }
    return null;
  };

  const employees = Array.isArray(totalStaff) ? totalStaff : [];

  const handleAddComment = useCallback(() => {
    if (!commentText.trim()) return;

    const currentUserName =
      user?.fullName ||
      user?.firstName ||
      user?.username ||
      "Unknown";

    const assignedText = commentEmployees.length
      ? `${currentUserName} set comment: "${commentText}" assigned to: ${commentEmployees
        .map((id) => {
          const employee = employees.find(
            (e) =>
              String(e.employeeId).toLowerCase().trim() ===
              String(id).toLowerCase().trim() ||
              `${e.firstName} ${e.lastName}`.toLowerCase().trim() ===
              String(id).toLowerCase().trim()
          );

          if (!employee) return id;

          const first = employee.firstName?.trim() || "";
          const last = employee.lastName?.trim() || "";

          return `${first} ${last}`.trim();
        })
        .join(", ")}`
      : `${currentUserName} commented: "${commentText}"`;

    const newComment = {
      _id: Date.now().toString(),
      text: assignedText,
      createdByuserId: user.userId,
      createdBy: currentUserName,
      createdAt: nowIST,
    };

    setEditData((prev) => ({
      ...prev,
      comments: prev.comments ? [...prev.comments, newComment] : [newComment],
    }));

    setCommentText("");
    setCommentEmployees([]);
    const taskId = currentTask?._id || editData?._id;
    const projectId = currentTask?.projectId || activeProjectId;

    if (!taskId || !projectId) return;

    const derivedSubProjectId =
      currentTask?.subProjectId ||
      findSubProjectIdByTask(projects, taskId) ||
      null;

    const payload = {
      projectId,
      taskId,
      data: {
        comments: [newComment],
        subProjectId: derivedSubProjectId,
      },
    };

    addComment.mutate(payload, {
      onSuccess: () => {
        refetch?.();
      },
      onError: (err) => {
        console.error("Failed to add comment:", err);
        setEditData((prev) => ({
          ...prev,
          comments: prev.comments.filter((c) => c._id !== newComment._id),
        }));
      },
    });
  }, [
    commentText,
    currentTask,
    editData,
    user,
    addComment,
    refetch,
    activeProjectId,
    projects,
    findSubProjectIdByTask,
  ]);

  const { data: comments, isLoading, isError, error } = useComments({
    projectId: activeProjectId,
    taskId: currentTask?._id,
    subProjectId: activeSubProjectObj?._id,
  });

  const mergedComments = useMemo(() => {
    if (comments && comments.length > 0) {
      return comments;
    }

    return editData.comments || [];
  }, [comments, editData.comments]);

  const [autoClickIdSubProject, setAutoClickIdSubProject] = useState(null);

  function handleTaskcheck(projectId, subProjectId, taskId, status) {
    setActive("list");
    setActiveProjectId(projectId);
    setActiveSubProjectId(subProjectId || null);
    setAutoClickIdSubProject(subProjectId);
    setCollapsedStatuses((prev) => ({
      ...prev,
      [status]: false,
    }));

    setTimeout(() => {
      const taskEl = document.getElementById(`${taskId}`);
      if (taskEl) {
        taskEl.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        console.warn(`No task element found with id ${taskId}`);
      }
    }, 100);
  }

  const [loginDetials, setLoginDetials] = useState("");

  useEffect(() => {
    if (activeSubProjectId) {
      const parentProject = projects.find(project =>
        project.subProjects?.some(sp => sp._id === activeSubProjectId)
      );
      const subProject = parentProject?.subProjects?.find(sp => sp._id === activeSubProjectId);
      setLoginDetials(subProject?.subloginDetials || "");
    } else {
      setLoginDetials(selectedProjectData?.loginDetials || "");
    }
  }, [activeSubProjectId, selectedProjectData, projects]);

  useEffect(() => {
    if (!projects.length) return;

    const params = new URLSearchParams(window.location.search);

    const projectId = params.get("projectId");
    const subProjectId = params.get("subProjectId");
    const taskId = params.get("taskId");
    const tab = params.get("tab");

    if (tab) setActive(tab);

    if (projectId) {
      const exists = projects.some(
        (p) => String(p._id || p.id) === String(projectId)
      );

      if (exists) {
        setActiveProjectId(String(projectId));
      }
    }

    if (subProjectId) {
      setActiveSubProjectId(String(subProjectId));
    }

    if (taskId) {
      setTimeout(() => {
        const el = document.getElementById(`${taskId}`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 500);
    }

  }, [projects]);

  if (!user || !existingPermissions) {
    return (
      <EmployeeLayout>
        <SkeletonPage />
      </EmployeeLayout>
    );
  }

  if (!project_view) {
    return (
      <EmployeeLayout>
        <div className="relative h-[90.7vh] overflow-hidden z-1">
          <PermissionPopup />
        </div>
      </EmployeeLayout>
    );
  }

  return (
    <EmployeeLayout>
      <div className="relative z-1">
        <div className="relative z-1">
          <ProjectTab
            setActive={setActive}
            active={active}
            setActiveSubProjectId={setActiveSubProjectId}
            setActiveProjectId={setActiveProjectId}
            user={user}
          />
          <div className="p-2 sm:p-6">
            {active !== "mytasks" && (activeProjectId || activeSubProjectId) && (
              <div className="inline-flex gap-2 items-center my-6 sm:my-4">
                <div
                  className="flex items-center gap-2 cursor-pointer back-btn"
                  onClick={() => {
                    if (activeSubProjectId) {
                      setActiveSubProjectId(null);

                      const params = new URLSearchParams(window.location.search);
                      params.delete("subProjectId");

                      const newUrl = `${window.location.pathname}?${params.toString()}`;
                      window.history.replaceState({}, "", newUrl);

                    } else {
                      setActiveProjectId(null);

                      const params = new URLSearchParams(window.location.search);
                      params.delete("projectId");

                      const newUrl = `${window.location.pathname}?${params.toString()}`;
                      window.history.replaceState({}, "", newUrl);
                    }
                  }}
                >
                  <MoveLeft size={20} />
                  <span className="font-[400]">
                    {activeSubProjectObj?.projectName
                      ? activeSubProjectObj.projectName
                      : selectedProjectData?.projectName || "Untitled Project"}
                  </span>
                </div>
                <div
                  className="inline-flex items-center gap-2 bg-zinc-800 text-rose-50 text-xs font-medium px-3 py-1 rounded-full shadow-sm select-none cursor-pointer hover:bg-zinc-700"
                  onClick={() => setShowModal(true)}

                >
                  Details <Info size={18} />
                </div>
              </div>
            )}

            {active === "mytasks" ? (
              <MyTask
                projects={projects}
                user={user}
                employees={totalStaff}
                formatStatusLabel={formatStatusLabel}
                getStatusColor={getStatusColor}
                TaskEmployees={TaskEmployees}
                onTaskClick={(projectId, subProjectId, taskId, status) => {
                  setActive("list");
                  handleTaskcheck(projectId, subProjectId, taskId, status);
                }}
                TaskPriority={TaskPriority}
              />
            ) : (
              !activeSubProjectId && (
                <ProjectList
                  projects={projects}
                  activeProjectId={activeProjectId}
                  setActiveProjectId={setActiveProjectId}
                  currentUser={user}
                  projectIds={projectIds}
                  activeSubProjectId={activeSubProjectId}
                  setActiveSubProjectId={setActiveSubProjectId}
                  selectedProjectData={selectedProjectData}
                  setAutoClickIdSubProject={setAutoClickIdSubProject}
                  autoClickIdSubProject={autoClickIdSubProject}
                  setActive={setActive}
                  setOrderedProjects={setOrderedProjects}
                  orderedProjects={orderedProjects}
                  selectProjectFilter={selectProjectFilter}
                  setSelectProjectFilter={setSelectProjectFilter}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                />
              )
            )}

            {active === "list" &&
              activeProjectId &&
              (!activeProject?.isMultiProject || activeSubProjectId) && (
                <div className="border border-gray-300 rounded-lg p-2 sm:p-4 bg-white shadow-sm ">
                  {projectStatuses.map((s) => (
                    <div key={s}>{renderSection(s)}</div>
                  ))}
                </div>
              )}
          </div>

          {showModal && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-2 sm:px-4"
              onClick={() => setShowModal(false)}
            >
              <div
                className="
        relative w-full max-w-3xl
        h-[60vh] sm:h-[75vh]
        bg-white rounded-xl sm:rounded-2xl
        shadow-2xl flex flex-col
        animate-scaleIn
      "
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="
          flex items-center justify-between
          px-3 py-2 sm:px-6 sm:py-4
          border-b bg-gray-50
          rounded-t-xl sm:rounded-t-2xl
        ">
                  <h3 className="
            text-base sm:text-xl
            font-semibold text-gray-800
          ">
                    Project Details
                  </h3>

                  <button
                    onClick={() => {
                      setShowModal(false);
                    }}
                    className="p-1.5 sm:p-2 rounded-full hover:bg-red-100 transition"
                  >
                    <X size={16} className="sm:w-[18px] sm:h-[18px] text-red-500" />
                  </button>
                </div>

                <div className="
          flex-1
          p-3 sm:p-6
          overflow-y-auto
          relative bg-white
        ">
                  <div className="h-full
              min-h-[250px] sm:min-h-[350px]
              rounded-lg sm:rounded-xl
              border border-gray-200
              p-3 sm:p-5
              bg-gray-50
              text-gray-700
              text-[12px] sm:sm
              leading-relaxed
              overflow-auto
              ql-editor
            ">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: loginDetials || "<p class='text-gray-400'>No login details</p>",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {showDescriptionPopup && currentTask && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onMouseDown={(e) => {
              if (popupRef.current && !popupRef.current.contains(e.target)) {
                setShowDescriptionPopup(false);
              }
            }}>
              <div
                ref={popupRef}
                className="bg-white rounded p-2 zs:p-6 w-[90%] h-[90vh] shadow-lg relative overflow-y-auto max-w-[90%] animate-scaleIn">
                <div className="w-full mx-auto flex justify-between h-[100%] border border-gray-300">
                  <div className="w-[100%] lg:w-[70%] border-r border-gray-400 p-[10px] xl:p-[20px] overflow-auto scrollbar-hide">
                    <h2 className="mb-4 text-gray-500 font-light inline-flex gap-[5px] items-center justify-center text-[12px] md:text-[16px] p-[5px] w-[80px] border border-gray-300 rounded-md">
                      <CircleDot size={12} /> Task
                    </h2>
                    <textarea
                      ref={textareaRef}
                      className="w-full rounded px-2 py-1 mb-4 text-[16px] sm:text-[20px] md:text-[25px] border-gray-400 outline-none focus:ring-1 focus:ring-gray-200 resize-none overflow-hidden"
                      value={editData.title}
                      // onChange={(e) =>
                      //   setEditData((prev) => ({
                      //     ...prev,
                      //     title: e.target.value,
                      //   }))
                      // }
                      rows={1}
                      placeholder="Enter text..."
                    />

                    <div className="text-[14px]">
                      <div className="flex items-center gap-3 xs:gap-[50px] mb-4">
                        <span className="flex items-center gap-[5px] min-w-[100px] zs:min-w-[150px]">
                          <CircleStop className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="font-medium text-[12px] sm:text-[16px]  text-gray-700">Status</span>
                        </span>

                        <div className="relative flex items-center gap-2">

                          <span
                            className={`
        px-3 py-1 rounded-md text-[12px] sm:text-[15px] md:text-[16px] font-semibold
        shadow-sm border
        ${getStatusColor(editData.status || "")}
      `}
                          >
                            {editData.status ? formatStatusLabel(editData.status) : "-"}
                          </span>

                          {status_update &&
                            allStatuses.length > 0 &&
                            currentTask?.assignedEmployees?.some(
                              (emp) => emp.userId === user?.userId
                            ) && (
                              <Pencil
                                size={14}
                                className="
        cursor-pointer text-orange-500
        hover:text-orange-600
        hover:scale-110
        transition-all
      "
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowStatusDropdownFor((prev) =>
                                    prev === "editPanel" ? null : "editPanel"
                                  );
                                }}
                              />
                            )}

                          {status_update &&
                            showStatusDropdownFor === "editPanel" &&
                            allStatuses.length > 0 && (

                              <div
                                ref={statusDropdownRef}
                                onClick={(e) => e.stopPropagation()}
                                className="
              absolute top-full left-[-67px] zx:left-0 mt-2
        w-44 max-h-56
        bg-white
        border border-gray-200
        rounded-xl
        shadow-xl
        overflow-hidden
        overflow-y-auto
        z-50
        flex flex-col
        animate-fadeIn
      "
                              >

                                {allStatuses
                                  .filter((s) => s !== "complete")
                                  .map((s) => (
                                    <div
                                      key={s}
                                      onClick={() => {
                                        handleBulkStatusUpdate(s, {
                                          ...currentTask,
                                          projectId: activeProjectId,
                                          subProjectId: activeSubProjectObj?._id || null,
                                        });

                                        setShowStatusDropdownFor(null);
                                        setEditData((prev) => ({ ...prev, status: s }));
                                      }}
                                      className={`
                px-4 py-2.5
              md:px-3 text-[11px] md:text-sm
                cursor-pointer
                flex items-center justify-between
                transition-all
                ${s === editData.status
                                          ? "bg-gray-800 text-white font-semibold"
                                          : "text-gray-700 hover:bg-gray-100"
                                        }
              `}
                                    >
                                      <span>{formatStatusLabel(s)}</span>

                                      {s === editData.status && (
                                        <span className="text-xs opacity-80">✓</span>
                                      )}
                                    </div>
                                  ))}

                              </div>
                            )}
                        </div>

                      </div>


                      <div className="flex items-center gap-3 xs:gap-[50px] mb-4 date-popup">
                        <span className="flex items-center gap-[5px] min-w-[100px] zs:min-w-[150px]">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="font-medium text-[12px] sm:text-[16px]  text-gray-700">Dates</span>
                        </span>
                        <div className="relative date-filter">
                          <div className="cursor-pointer flex items-center gap-2 border border-gray-300 text-gray-400 text-sm rounded-lg px-3 py-1 bg-white hover:shadow-sm transition-shadow">
                            <div className="hidden custom:block text-gray-600 text-sm">
                              {editData.dueDate
                                ? new Date(editData.dueDate).toLocaleString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                })
                                : "-"}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 xs:gap-[50px] mb-4">
                        <span className="flex items-center gap-[5px] min-w-[100px] zs:min-w-[150px]">
                          <Flag className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="font-medium text-[12px] sm:text-[16px]  text-gray-700">
                            Priority
                          </span>
                        </span>
                        <div className="relative">
                          <TaskPriority value={editData.priority} onChange={({})} showclear={false} />
                        </div>
                      </div>

                      <div className="flex items-center gap-[50px] mb-4">
                        <span className="flex items-center gap-[5px] min-w-[100px] zs:min-w-[150px]">
                          <Users size={16} />
                          <span className="font-medium text-gray-700">Assigned</span>
                        </span>
                        <div className="relative flex items-center gap-[10px]">
                          <TaskEmployees
                            selected={editData.assignedEmployees?.map(e => e.userId) || []}
                            employees={totalStaff || []}
                            showDropdown={false}
                            hideSelected={true}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-3 xs:gap-[50px] mb-4">
                        <span className="flex items-center gap-[5px] min-w-[100px] zs:min-w-[150px]">
                          <ClockFading className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="font-medium text-[12px] sm:text-[16px]  text-gray-700">
                            Track time
                          </span>
                        </span>
                        <div className="relative flex items-center gap-[10px]">
                          <TaskTimeline
                            task={currentTask}
                            user={user}
                            handleTimelineUpdate={handleTimelineUpdate}
                            showControls={false}
                            showDropDown={true}
                            employee={totalStaff}
                          />
                        </div>
                      </div>

                    </div>

                    <div className="w-full mx-auto mt-10">
                      <TaskDetails editData={editData} setEditData={setEditData} user={user} TaskEmployees={TaskEmployees} employees={employees} />
                    </div>

                    <button onClick={() => setShowDescriptionPopup(false)} className="
    absolute top-1 right-1
    flex items-center justify-center
    w-8 h-8
    rounded-full
    bg-gray-200 hover:bg-gray-300
    text-gray-700
    focus:outline-none focus:ring-2 focus:ring-gray-400
    transition-colors
  "
                      aria-label="Close popup"
                    >
                      <X size={18} strokeWidth={2.5} />
                    </button>
                  </div>

                  <div className="w-[30%] hidden lg:block">
                    <div className="p-[5px] xl:p-[10px] border-b border-gray-400 text-sm font-medium">
                      Activity
                    </div>
                    <div className="p-[5px] xl:p-[5px] text-[12px] bg-[#f9f9f9]">
                      <CommentsSection
                        projects={projects}
                        comments={mergedComments}
                      />
                      <div className="w-full flex items-end gap-2 bg-gray-50 border rounded-xl p-2 sm:p-3 shadow-sm">
                        <TaskEmployees
                          employees={employees}
                          selected={commentEmployees}
                          onChange={(arr) => {
                            setCommentEmployees(arr);
                          }}
                          placement="top"
                          align="left"
                        />

                        <textarea
                          className="flex-1 border border-gray-300 rounded-lg px-2 py-1 sm:px-3 sm:py-2 text-sm
  focus:outline-none focus:ring-2 focus:ring-blue-400
  placeholder-gray-400 bg-white resize-none  min-h-[50px] overflow-y-auto"

                          placeholder="Write a comment..."
                          value={commentText}
                          rows={1}
                          onChange={(e) => setCommentText(e.target.value)}

                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey && commentText.trim()) {
                              e.preventDefault();
                              handleAddComment();
                            }
                          }}
                        />
                        <button
                          className=" h-7 w-7 sm:h-9 sm:w-9 flex items-center justify-center
             bg-blue-600 text-white rounded-md
             hover:bg-blue-700 transition disabled:opacity-50"
                          disabled={!commentText.trim()}
                          onClick={() => {
                            if (commentText.trim() !== "") handleAddComment();
                          }}
                        >
                          <SendHorizontal size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </EmployeeLayout>
  );
}
